<!-- src/admin/pages/DashboardPage.vue -->
<template>
  <div class="dashboard-container">
    <!-- User Info Card -->
    <div class="user-info-card">
      <h2>Account Information</h2>
      <p><strong>Email:</strong> {{ userEmail }}</p>
      <p><strong>Role:</strong> <span class="role-badge" :class="userRole">{{ userRole }}</span></p>
    </div>

    <!-- Main Content (right column) -->
    <main class="main-content">
      <!-- Overview Header -->
      <header class="overview-header">
        <h1>Application Submissions Overview</h1>
      </header>

      <!-- Stat Cards (active applications; click to filter the list, click again to clear) -->
      <section class="stats-cards">
        <button
          v-for="card in statCards"
          :key="card.key"
          type="button"
          class="card"
          :class="[`card--${card.tone}`, { 'card--active': cardFilter === card.key }]"
          :aria-pressed="cardFilter === card.key"
          @click="toggleCardFilter(card.key)"
        >
          <p class="card-title">{{ card.label }}</p>
          <p class="card-value">{{ card.count }}</p>
        </button>
      </section>

      <!-- Recent Applications Table -->
      <section class="recent-projects">
        <div class="section-header">
          <h2 class="section-title">Recent Applications</h2>
          <!-- Sort Control -->
          <div class="sort-controls">
            <label for="sortOrderSelect">Sort by Date:</label>
            <select id="sortOrderSelect" v-model="sortOrder">
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>

        <!-- Search Hint -->
        <div class="search-hint" :class="{ 'search-hint--archive': showArchivedApplications }">
          <i :class="showArchivedApplications ? 'fas fa-archive' : 'fas fa-info-circle'"></i>
          <span v-if="showArchivedApplications">You are searching <strong>archived</strong> applications. Uncheck <strong>Show Archived</strong> to return to active applications.</span>
          <span v-else>Search applies to active applications. Enable <strong>Show Archived</strong> below to search archived records.</span>
        </div>

        <!-- Search Bar -->
        <div class="search-bar">
          <div class="search-input-wrapper" :class="{ 'search-input-wrapper--archive': showArchivedApplications }">
            <i :class="showArchivedApplications ? 'fas fa-archive search-icon' : 'fas fa-search search-icon'"></i>
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="showArchivedApplications ? 'Search archived applications...' : 'Search by name or email...'"
              class="search-input"
            />
            <span v-if="showArchivedApplications" class="search-mode-badge">Archived</span>
            <button v-if="searchQuery" class="search-clear" :class="{ 'search-clear--shifted': showArchivedApplications }" @click="searchQuery = ''">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <span v-if="searchQuery" class="search-results-count">
            {{ sortedSubmissions.length }} result(s) found
          </span>
        </div>

        <div v-if="activeCard" class="card-filter-chip">
          Showing: <strong>{{ activeCard.label }}</strong> ({{ sortedSubmissions.length }})
          <button type="button" @click="cardFilter = null" aria-label="Clear filter">✕</button>
        </div>

        <!-- Archive Toggle & Bulk Actions -->
        <div class="table-controls">
          <div class="archive-toggle">
            <label>
              <input type="checkbox" v-model="showArchivedApplications" />
              Show Archived ({{ submissions.filter(app => app.status === 'Archived' && !app.removed).length }})
            </label>
            <label class="removed-toggle">
              <input type="checkbox" v-model="showRemovedApplications" />
              Show Removed ({{ submissions.filter(app => app.removed).length }})
            </label>
          </div>

          <div v-if="selectedApplications.length > 0" class="bulk-actions">
            <span class="selection-count">{{ selectedApplications.length }} application(s) selected</span>
            <button class="bulk-archive-button" @click="bulkArchiveApplications">
              <i class="fas fa-archive"></i> Archive Selected
            </button>
            <button class="bulk-cancel-button" @click="clearSelection">Cancel</button>
          </div>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th style="width: 40px;">
                  <input type="checkbox" @change="toggleSelectAll" :checked="allApplicationsSelected" />
                </th>
                <th>#</th>
                <th>Applicant Name</th>
                <th>Email</th>
                <th>Address</th>
                <th>Date Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <!-- Loop over paginatedSubmissions instead of all sortedSubmissions -->
              <tr v-for="(sub, idx) in paginatedSubmissions" :key="sub.id">
                <td>
                  <input type="checkbox" :checked="selectedApplications.includes(sub.id)" 
                    @change="toggleApplicationSelection(sub.id)" />
                </td>
                <td>{{ isFinite(itemsPerPage) ? (currentPage - 1) * itemsPerPage + idx + 1 : idx + 1 }}</td>
                <td>{{ sub.firstName }} {{ sub.lastName }}</td>
                <td>{{ sub.email }}</td>
                <td>{{ sub.address }}</td>
                <td>{{ formatDate(sub.submittedAt) }}</td>
                <td>
                  <span class="status-badge" :class="sub.status">
                    {{ sub.status }}
                  </span>
                </td>
                <td class="actions-cell">
                  <router-link :to="{ name: 'ApplicationView', params: { id: sub.id } }" class="view-link">
                    View
                  </router-link>
                  <button v-if="sub.status !== 'Archived'" @click="archiveApplicant(sub.id)" class="archive-btn" title="Archive applicant">
                    Archive
                  </button>
                  <button v-else @click="unarchiveApplicant(sub.id)" class="unarchive-btn" title="Restore applicant">
                    Restore
                  </button>
                  <button @click="deleteApplication(sub.id, sub.firstName, sub.lastName)" class="delete-btn" title="Delete application">
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination Controls -->
        <div class="pagination-controls">
          <button @click="previousPage" :disabled="currentPage === 1" class="pagination-btn">
            ← Previous
          </button>
          <div class="page-info">
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <select v-model.number="itemsPerPage" class="items-per-page-select">
              <option :value="5">5 per page</option>
              <option :value="10">10 per page</option>
              <option :value="15">15 per page</option>
              <option :value="25">25 per page</option>
              <option :value="Infinity">Show All</option>
            </select>
          </div>
          <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
            Next →
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { collection, onSnapshot, query, where, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore'
import { db, auth } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'

const router = useRouter()
const submissions = ref([])
const userEmail = ref('')
const userRole = ref('')
let unsubscribeSnapshot = null

// Add a reactive variable to track sort order: "asc" or "desc"
const sortOrder = ref('desc')

// Pagination state
const currentPage = ref(1)
const itemsPerPage = ref(10)

// Archive state
const selectedApplications = ref([])
const showArchivedApplications = ref(false)
const showRemovedApplications = ref(false) // applications of users removed from the Users page
const cardFilter = ref(null) // stat card currently filtering the list (null = none)

// Search state
const searchQuery = ref('')

// Fuzzy match: checks if all characters in the query appear in order within the target string
function fuzzyMatch(target, query) {
  if (!target || !query) return false
  const t = target.toLowerCase()
  const q = query.toLowerCase()
  // First check simple includes (substring match)
  if (t.includes(q)) return true
  // Fuzzy: all query chars appear in order in target
  let ti = 0
  for (let qi = 0; qi < q.length; qi++) {
    const found = t.indexOf(q[qi], ti)
    if (found === -1) return false
    ti = found + 1
  }
  return true
}

// Reset page to 1 when search query changes
watch(searchQuery, () => {
  currentPage.value = 1
})

// Switching between active/archived/removed views: back to page 1, drop selections from the old view
watch([showArchivedApplications, showRemovedApplications, cardFilter], () => {
  currentPage.value = 1
  selectedApplications.value = []
})
// Turning on the archived/removed view clears the card filter (cards only describe active applications)
watch([showArchivedApplications, showRemovedApplications], ([archived, removed]) => {
  if (archived || removed) cardFilter.value = null
})

onMounted(() => {
  // Ensure user is authenticated; otherwise redirect to login
  const unwatch = onAuthStateChanged(auth, async (user) => {
    if (!user) {
      router.replace({ name: 'Login' })
    } else {
      // Fetch user email and role
      userEmail.value = user.email
      try {
        // Query users collection by uid field
        const q = query(collection(db, 'users'), where('uid', '==', user.uid))
        const querySnapshot = await getDocs(q)
        if (!querySnapshot.empty) {
          userRole.value = querySnapshot.docs[0].data().role || 'N/A'
        } else {
          // Fallback: try searching by email
          const emailQuery = query(collection(db, 'users'), where('email', '==', user.email))
          const emailSnapshot = await getDocs(emailQuery)
          if (!emailSnapshot.empty) {
            userRole.value = emailSnapshot.docs[0].data().role || 'N/A'
          }
        }
      } catch (err) {
        console.error('Error fetching user role:', err)
      }

      // Listen to the entire 'applications' collection
      unsubscribeSnapshot = onSnapshot(
        collection(db, 'applications'),
        (snap) => {
          submissions.value = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        }
      )
    }
    unwatch() // stop listening to auth changes after first check
  })
})

onUnmounted(() => {
  if (unsubscribeSnapshot) {
    unsubscribeSnapshot()
  }
})

// Computed stats remain the same
const visibleSubmissions = computed(() => submissions.value.filter(a => !a.removed))

// ---- Stat cards: each one is also a filter on the active (non-archived) list ----
const WEEK_MS = 7 * 24 * 60 * 60 * 1000
function submittedMs(a) {
  if (!a.submittedAt) return 0
  return a.submittedAt.seconds ? a.submittedAt.seconds * 1000 : new Date(a.submittedAt).getTime() || 0
}
const CARD_FILTERS = {
  all: { label: 'All Active', tone: 'slate', match: () => true },
  new: { label: 'New This Week', tone: 'blue', match: a => Date.now() - submittedMs(a) < WEEK_MS },
  Pending: { label: 'Pending', tone: 'amber', match: a => a.status === 'Pending' },
  InReview: { label: 'In Review', tone: 'violet', match: a => a.status === 'InReview' },
  Accepted: { label: 'Accepted', tone: 'green', match: a => a.status === 'Accepted' },
  Converted: { label: 'Converted to Employee', tone: 'teal', match: a => a.status === 'Converted to Employee' },
}
const activeSubmissions = computed(() => visibleSubmissions.value.filter(a => a.status !== 'Archived'))
const statCards = computed(() => Object.entries(CARD_FILTERS).map(([key, c]) => ({
  key, label: c.label, tone: c.tone, count: activeSubmissions.value.filter(c.match).length
})))
const activeCard = computed(() => statCards.value.find(c => c.key === cardFilter.value) || null)

function toggleCardFilter(key) {
  cardFilter.value = cardFilter.value === key ? null : key
  // Cards describe active applications, so leave the archived/removed views
  showArchivedApplications.value = false
  showRemovedApplications.value = false
}

// Computed sortedSubmissions: sorts by submittedAt timestamp, filters by removed/archive status and search
const sortedSubmissions = computed(() => {
  // Removed applications only show under "Show Removed" (regardless of archive status)
  if (showRemovedApplications.value) {
    return sortBySubmitted(submissions.value.filter(a => a.removed && matchesSearch(a)))
  }

  // Create a shallow copy so we don't mutate the original array
  let filtered = visibleSubmissions.value.slice()

  // Filter by archive status
  if (showArchivedApplications.value) {
    filtered = filtered.filter(a => a.status === 'Archived')
  } else {
    filtered = filtered.filter(a => a.status !== 'Archived')
    if (cardFilter.value) filtered = filtered.filter(CARD_FILTERS[cardFilter.value].match)
  }

  return sortBySubmitted(filtered.filter(matchesSearch))
})

// Fuzzy search on name/email (passes everything when the search box is empty)
function matchesSearch(a) {
  const q = searchQuery.value.trim()
  if (!q) return true
  return fuzzyMatch(a.firstName, q) ||
    fuzzyMatch(a.lastName, q) ||
    fuzzyMatch(`${a.firstName} ${a.lastName}`, q) ||
    fuzzyMatch(a.email, q)
}

// Sort by submitted date in the chosen order
function sortBySubmitted(list) {
  return list.sort((a, b) => {
    // Extract Unix seconds or fallback to Date parsing
    const aTime = a.submittedAt
      ? a.submittedAt.seconds || new Date(a.submittedAt).getTime() / 1000
      : 0
    const bTime = b.submittedAt
      ? b.submittedAt.seconds || new Date(b.submittedAt).getTime() / 1000
      : 0

    if (sortOrder.value === 'asc') {
      return aTime - bTime
    } else {
      return bTime - aTime
    }
  })
}

// Computed for checkbox select all state
const allApplicationsSelected = computed(() =>
  paginatedSubmissions.value.length > 0 && selectedApplications.value.length === paginatedSubmissions.value.length
)

// Computed pagination properties
const totalPages = computed(() => {
  return Math.ceil(sortedSubmissions.value.length / itemsPerPage.value) || 1
})

const paginatedSubmissions = computed(() => {
  // If itemsPerPage is Infinity (Show All), return all records
  if (!isFinite(itemsPerPage.value)) {
    return sortedSubmissions.value
  }
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return sortedSubmissions.value.slice(start, end)
})

// Pagination methods
function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

function previousPage() {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

// Archive applicant
async function archiveApplicant(applicantId) {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Archive Applicant',
    text: 'Are you sure you want to archive this applicant? They will no longer appear in the active list.',
    showCancelButton: true,
    confirmButtonText: 'Yes, Archive',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
  })

  if (!result.isConfirmed) return

  try {
    const applicantRef = doc(db, 'applications', applicantId)
    await updateDoc(applicantRef, {
      status: 'Archived',
      archivedAt: new Date(),
    })

    await Swal.fire({
      icon: 'success',
      title: 'Archived',
      text: 'Applicant has been archived successfully.',
    })
  } catch (err) {
    console.error('Error archiving applicant:', err)
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to archive applicant. Please try again.',
    })
  }
}

// Unarchive applicant
async function unarchiveApplicant(applicantId) {
  const result = await Swal.fire({
    icon: 'info',
    title: 'Restore Applicant',
    text: 'Restore this applicant to active applications?',
    showCancelButton: true,
    confirmButtonText: 'Yes, Restore',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#10b981',
  })

  if (!result.isConfirmed) return

  try {
    const applicantRef = doc(db, 'applications', applicantId)
    await updateDoc(applicantRef, {
      status: 'Pending',
    })

    await Swal.fire({
      icon: 'success',
      title: 'Restored',
      text: 'Applicant has been restored successfully.',
    })
  } catch (err) {
    console.error('Error restoring applicant:', err)
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to restore applicant. Please try again.',
    })
  }
}

// Delete application
async function deleteApplication(applicantId, firstName, lastName) {
  const result = await Swal.fire({
    icon: 'warning',
    title: 'Delete Application',
    html: `<p>Are you sure you want to delete <strong>${firstName} ${lastName}</strong>'s application?</p><p style="color: #dc2626; font-size: 12px;">This action cannot be undone. All application records will be permanently deleted.</p>`,
    showCancelButton: true,
    confirmButtonText: 'Yes, Delete Application',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626',
  })

  if (!result.isConfirmed) return

  Swal.fire({
    title: 'Deleting...',
    html: '<div class="custom-loader"></div>',
    allowOutsideClick: false,
    showConfirmButton: false,
  })

  try {
    await deleteDoc(doc(db, 'applications', applicantId))

    await Swal.fire({
      icon: 'success',
      title: 'Deleted',
      text: `${firstName} ${lastName}'s application has been permanently deleted.`,
      confirmButtonColor: '#10b981',
      timer: 2000,
      timerProgressBar: true
    })
  } catch (error) {
    console.error('Error deleting application:', error)
    Swal.fire({
      icon: 'error',
      title: 'Delete Failed',
      text: 'Error deleting application. Please try again.',
      confirmButtonColor: '#ef4444'
    })
  }
}

// Selection methods
function toggleApplicationSelection(applicantId) {
  const idx = selectedApplications.value.indexOf(applicantId)
  if (idx > -1) {
    selectedApplications.value.splice(idx, 1)
  } else {
    selectedApplications.value.push(applicantId)
  }
}

function toggleSelectAll() {
  if (allApplicationsSelected.value) {
    selectedApplications.value = []
  } else {
    selectedApplications.value = paginatedSubmissions.value.map(app => app.id)
  }
}

function clearSelection() {
  selectedApplications.value = []
}

// Bulk archive
async function bulkArchiveApplications() {
  if (selectedApplications.value.length === 0) return

  const archivedCount = selectedApplications.value.length

  const result = await Swal.fire({
    title: `Archive ${archivedCount} Application(s)?`,
    html: '<p>These applications will be moved to archives.</p>',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Archive All',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#ef4444',
  })

  if (!result.isConfirmed) return

  Swal.fire({
    title: 'Archiving...',
    html: '<div class="custom-loader"></div>',
    allowOutsideClick: false,
    showConfirmButton: false,
  })

  try {
    const updates = selectedApplications.value.map(id =>
      updateDoc(doc(db, 'applications', id), {
        status: 'Archived',
        archivedAt: new Date(),
      })
    )
    await Promise.all(updates)

    clearSelection()
    await Swal.fire('Success!', `${archivedCount} application(s) archived.`, 'success')
  } catch (err) {
    console.error('Error archiving applications:', err)
    await Swal.fire('Error', 'Failed to archive some applications.', 'error')
  }
}

// Helper to format Firestore Timestamp (or JS date‐string) into a human‐readable string
function formatDate(val) {
  if (!val) return '—'
  const d = val.seconds ? new Date(val.seconds * 1000) : new Date(val)
  return `${d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} ${d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })}`
}
</script>

<style scoped>
/* Container */
.dashboard-container {
  display: flex;
  flex-direction: column;
  background-color: #f0f4f8;
}

/* User Info Card */
.user-info-card {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin: 24px;
  margin-bottom: 0;
}

.user-info-card h2 {
  font-size: 18px;
  margin-bottom: 12px;
  color: #1f2937;
}

.user-info-card p {
  margin: 8px 0;
  color: #374151;
}

.role-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.role-badge.admin {
  background-color: #dbeafe;
  color: #1e40af;
}

.role-badge.payroll {
  background-color: #dcfce7;
  color: #166534;
}

.role-badge.superadmin {
  background-color: #fef3c7;
  color: #92400e;
}

.role-badge.user {
  background-color: #f3e8ff;
  color: #6b21a8;
}

/* Main Content */
.main-content {
  flex: 1;
  padding: 24px;
}

/* Overview Header */
.overview-header h1 {
  font-size: 24px;
  margin-bottom: 24px;
  color: #1f2937;
}

/* Stat Cards */
.stats-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 32px;
}

.card {
  flex: 1;
  min-width: 150px;
  background-color: #ffffff;
  border: 2px solid transparent;
  border-top: 4px solid var(--card-accent, #64748b);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 16px;
  text-align: center;
  font: inherit;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
}

.card:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.card--active {
  border-color: var(--card-accent, #64748b);
  background-color: #f8fafc;
}

.card--slate { --card-accent: #64748b; }
.card--blue { --card-accent: #2563eb; }
.card--amber { --card-accent: #d97706; }
.card--violet { --card-accent: #7c3aed; }
.card--green { --card-accent: #16a34a; }
.card--teal { --card-accent: #0d9488; }

.card-filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  padding: 6px 8px 6px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  color: #1e3a8a;
  font-size: 14px;
}

.card-filter-chip button {
  border: none;
  background: transparent;
  color: #1e3a8a;
  cursor: pointer;
  font-size: 14px;
  padding: 0 4px;
}

.card-title {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
}

.card-value {
  font-size: 32px;
  font-weight: bold;
  color: #111827;
  margin: 0;
}

/* Section Header (Title + Sort Control) */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  color: #111827;
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sort-controls label {
  font-size: 14px;
  color: #374151;
}

.sort-controls select {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 14px;
}

/* Search Hint */
.search-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 13px;
  color: #1e40af;
  transition: all 0.3s ease;
}

.search-hint--archive {
  background-color: #fffbeb;
  border-color: #fcd34d;
  color: #92400e;
}

.search-hint i {
  font-size: 14px;
  flex-shrink: 0;
}

/* Search Bar */
.search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 400px;
  transition: all 0.3s ease;
}

.search-input-wrapper--archive .search-input {
  border-color: #f59e0b;
  background-color: #fffbeb;
}

.search-input-wrapper--archive .search-input:focus {
  border-color: #d97706;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15), 0 1px 2px rgba(0, 0, 0, 0.05);
}

.search-icon {
  position: absolute;
  left: 14px;
  color: #9ca3af;
  font-size: 14px;
  pointer-events: none;
  transition: color 0.2s ease;
}

.search-input-wrapper:focus-within .search-icon {
  color: #5867dd;
}

.search-input-wrapper--archive .search-icon {
  color: #d97706;
}

.search-input-wrapper--archive:focus-within .search-icon {
  color: #b45309;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.3s ease;
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-input:focus {
  outline: none;
  border-color: #5867dd;
  box-shadow: 0 0 0 3px rgba(88, 103, 221, 0.15), 0 1px 2px rgba(0, 0, 0, 0.05);
}

.search-mode-badge {
  position: absolute;
  right: 40px;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background-color: #f59e0b;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  letter-spacing: 0.025em;
  text-transform: uppercase;
  pointer-events: none;
}

.search-clear {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: #9ca3af;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-clear--shifted {
  right: 100px;
}

.search-clear:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.search-results-count {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

/* Table Controls */
.table-controls {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.archive-toggle {
  display: flex;
  align-items: center;
  gap: 16px;
}

.archive-toggle label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  font-weight: 500;
}

.archive-toggle input[type="checkbox"] {
  cursor: pointer;
}

/* Bulk Actions */
.bulk-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: #f3f4f6;
  border-radius: 6px;
}

.selection-count {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

.bulk-archive-button {
  background-color: #ef4444;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.bulk-archive-button:hover {
  background-color: #dc2626;
}

.bulk-archive-button:active {
  background-color: #b91c1c;
}

.bulk-cancel-button {
  background-color: #e5e7eb;
  color: #374151;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.bulk-cancel-button:hover {
  background-color: #d1d5db;
}

/* Recent Applications Table */
.table-wrap {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  background-color: #ffffff;
}

thead {
  background-color: #e5e7eb;
}

th,
td {
  padding: 12px 16px;
  text-align: left;
  color: #374151;
}

th {
  font-size: 12px;
  text-transform: uppercase;
  color: #6b7280;
}

tbody tr {
  border-bottom: 1px solid #e5e7eb;
}

th input[type="checkbox"],
td input[type="checkbox"] {
  cursor: pointer;
}

/* Status Badge */
.status-badge {
  display: inline-block;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 9999px;
}

.status-badge.Approved {
  background-color: #d1fae5;
  color: #065f46;
}

.status-badge.Pending {
  background-color: #fef3c7;
  color: #92400e;
}

.status-badge.InReview {
  background-color: #fef3c7;
  color: #92400e;
}

/* “View” link styling */
.view-link {
  color: #5867dd;
  font-weight: 500;
  text-decoration: none;
  margin-right: 8px;
}

.view-link:hover {
  text-decoration: underline;
}

/* Actions Cell */
.actions-cell {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

/* Archive Button */
.archive-btn {
  background-color: #ef4444;
  color: white;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.archive-btn:hover {
  background-color: #dc2626;
}

.archive-btn:active {
  background-color: #b91c1c;
}

/* Unarchive Button */
.unarchive-btn {
  background-color: #10b981;
  color: white;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.unarchive-btn:hover {
  background-color: #059669;
}

.unarchive-btn:active {
  background-color: #047857;
}

/* Delete Button */
.delete-btn {
  background-color: #ef4444;
  color: white;
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.delete-btn:hover {
  background-color: #dc2626;
}

.delete-btn:active {
  background-color: #b91c1c;
}

/* Pagination Controls */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
  padding: 16px;
  background-color: #ffffff;
  border-radius: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background-color: #ffffff;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #374151;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f9fafb;
}

.page-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #374151;
}

.items-per-page-select {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background-color: #ffffff;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.items-per-page-select:hover {
  border-color: #9ca3af;
}

.items-per-page-select:focus {
  outline: none;
  border-color: #5867dd;
  box-shadow: 0 0 0 2px rgba(88, 103, 221, 0.1);
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    padding: 16px;
  }

  .stats-cards {
    flex-direction: column;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .actions-cell {
    flex-direction: column;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 12px;
  }

  .pagination-btn {
    width: 100%;
  }
}
</style>
